import { Component, OnInit, ViewContainerRef } from "@angular/core";
import { PaginatedContent } from "../../../../api/common.types";
import {
  AccountFilter,
  AccountId,
  Filter,
  getStatusShortName,
  GroupFilter,
  MissionPartnerAccount,
  TalentAccount,
} from "../../home-admin-data/account.types";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { AccountService } from "../../home-admin-data/account.service";
import { NzTableQueryParams } from "ng-zorro-antd/table";
import { map, Observable, shareReplay, take } from "rxjs";
import { BreakpointObserver } from "@angular/cdk/layout";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NzSelectOptionInterface } from "ng-zorro-antd/select";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzModalService } from "ng-zorro-antd/modal";
import { TranslateService } from "@ngx-translate/core";
import { AccountPasswordDialogComponent } from "../../../../../../../common-ui/src/lib/account-password-dialog/account-password-dialog.component";
import { AccountStatus, AccountType } from "../../../login-page/login-page-data/login.types";
import { DatePipe } from "@angular/common";
import { AccountCommentsDialogComponent } from "../../../../../../../common-ui/src/lib/account-comments-dialog/account-comments-dialog.component";
import { Clipboard } from "@angular/cdk/clipboard";
import { FilterStorageService } from "../../../../api/filter-storage/filter-storage.service";
import { FiltersPage } from "../../../../api/filter-storage/filter-storage.types";
import { CmsCachedService } from "libs/platform-pages/src/lib/api/cms/cms-cached.service";
import { TableStateManager } from "../../utils/table-state-manager";
import { toTranslatedOptions } from "../../utils/translated-options";
import { PermissionsService } from "../../../../api/authorization/permissions.service";
import { Inject } from "@angular/core";
import { APP_CONFIGURATION } from "../../../../../../../core/src/lib/core.di";
import { AppConfig } from "libs/core/src/lib/app-config/config.types";

const FILTERS_PAGE = FiltersPage.MISSION_PARTNER_LIST;
// keep in sync with the media query in admin-mission-partner-list.component.less
const MOBILE_LAYOUT_QUERY = "(max-width: 1423px)";

@Component({
  selector: "mh-admin-mission-partner-list",
  templateUrl: "./admin-mission-partner-list.component.html",
  styleUrls: ["./admin-mission-partner-list.component.less"],
})
export class AdminMissionPartnerListComponent implements OnInit {
  tableState = new TableStateManager<MissionPartnerAccount>((filter) => this.accountService.loadMissionPartnerList(filter), (res) => {
    this.getSearchField("timeRange")?.patchValue(res.timeRange);
  });
  
  isApiLoading = false;
  searchForm!: UntypedFormGroup;

  stageOptions$: Observable<NzSelectOptionInterface[]>;
  isMobileLayout$: Observable<boolean>;
  canWrite = false;

  constructor(
    private fb: UntypedFormBuilder,
    private accountService: AccountService,
    private filterStorageService: FilterStorageService,
    private notificationService: NzNotificationService,
    private modal: NzModalService,
    private viewContainerRef: ViewContainerRef,
    private readonly translateService: TranslateService,
    private datePipe: DatePipe,
    private clipboard: Clipboard,
    private cmsCachedService: CmsCachedService,
    @Inject(APP_CONFIGURATION) readonly appConfig: AppConfig,
    private breakpointObserver: BreakpointObserver,
    private permissionsService: PermissionsService,
  ) {
    // shared by the stage select of every row
    this.stageOptions$ = toTranslatedOptions(
      this.cmsCachedService.getUserDataByName("mp-stage"),
      this.translateService,
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }));
    // render only one layout instead of hiding the other with CSS, both are heavy with 50 rows
    this.isMobileLayout$ = this.breakpointObserver.observe(MOBILE_LAYOUT_QUERY).pipe(
      map((state) => state.matches),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    // checked once for the page instead of a mhHasPermission directive (and store subscription) per row button
    this.permissionsService
      .hasPermission("mp_list.write")
      .pipe(takeUntilDestroyed())
      .subscribe((canWrite) => (this.canWrite = canWrite));
  }

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      searchTerm: [null],
      statusReviewed: [null],
      timeRange: [[]],
    });

    const storedFilters = this.filterStorageService.getStoredFilters()?.[FILTERS_PAGE];
    if (storedFilters) {
      this.searchForm.patchValue(storedFilters);
    }

    this.tableState.fetchData({ ...this.getDefaultQueryParams(), ...this.getFilterFormValues() });
  }

  onPageChange(pageIndex: number): void {
    this.tableState.onPageChange(pageIndex, () => this.getFilterFormValues());
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.tableState.onQueryParamsChange(params, () => this.getFilterFormValues());
  }

  submitFilterForm(): void {
    this.filterStorageService.saveFilters(this.searchForm, FILTERS_PAGE);
    this.tableState.onQueryChange({
      paging: {
        page: 1,
        itemsOnPage: this.tableState.data?.paging?.itemsOnPage || 0,
      },
      ...this.getFilterFormValues(),
    } as AccountFilter);
  }

  getFilterFormValues() {
    const searchField = this.getSearchField("searchTerm");
    const timeRangeField = this.getSearchField("timeRange");
    const statusReviewed = this.getSearchField("statusReviewed");

    const filterFormValues = {
      searchTerm: searchField?.value || "",
      timeRange: timeRangeField?.value || [],
      groupFilters: [] as GroupFilter[],
      filters: [] as Filter[],
    };

    if (statusReviewed?.value === "REVIEWED") {
      filterFormValues.filters?.push({
        value: AccountStatus.REVIEWED,
        field: "ACCOUNT_STATUS",
      });
    }

    if (statusReviewed?.value === "NOT_REVIEWED") {
      filterFormValues.filters?.push({
        value: AccountStatus.REVIEWED,
        field: "EXCLUDED_ACCOUNT_STATUS",
      });
    }

    return filterFormValues;
  }

  getSearchField(name: string) {
    return this.searchForm.get(name);
  }

  onSignInAs(accountId: string) {
    this.accountService.signInAs(accountId);
  }

  sendPasswordResetEmail(accountId: AccountId, email: string) {
    this.accountService.sendPasswordResetEmail(accountId, email).subscribe((success) => {
      if (success) {
        this.notificationService.success("", this.translateService.instant("admin.password-reset.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  openSetPasswordModal(account: TalentAccount | MissionPartnerAccount) {
    const modal = this.modal.create({
      nzContent: AccountPasswordDialogComponent,
      nzMaskClosable: false,
      nzViewContainerRef: this.viewContainerRef,
      nzData: { account },
    });
  }

  openCommentModal(account: TalentAccount | MissionPartnerAccount) {
    const modal = this.modal.create({
      nzContent: AccountCommentsDialogComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzCentered: true,
      nzWidth: "800px",
      nzStyle: { padding: "30px 0" },
      nzBodyStyle: { padding: "24px 24px 0" },
      nzMaskClosable: false,
      nzData: { account },
      nzFooter: null,
    });
  }

  deleteAccount(accountId: AccountId, accountType = AccountType.MISSION_PARTNER) {
    this.accountService.deleteAccount(accountId, accountType).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.filter(
            (account) => account.accountId.internalId !== accountId.internalId,
          );
        }
        this.notificationService.success("", this.translateService.instant("admin.account-delete.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  showDeleteConfirm(account: TalentAccount | MissionPartnerAccount) {
    this.modal.confirm({
      nzTitle: this.translateService.instant("admin.account-delete.confirm.title"),
      nzContent: `<b>${this.translateService.instant("admin.account-delete.confirm.text")}</b>`,
      nzOkText: this.translateService.instant("admin.account-delete.confirm.ok.btn"),
      nzCancelText: this.translateService.instant("admin.account-delete.confirm.cancel.btn"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.deleteAccount(account.accountId),
    });
  }

  downloadData(accountId: AccountId, accountType = AccountType.MISSION_PARTNER) {
    const currentDate = new Date();
    const formattedDate = this.datePipe.transform(currentDate, "yyyy-MM-dd");
    const fileName = `${formattedDate}-${accountId.internalId}_export.json`;

    this.accountService.downloadData(accountId, accountType, fileName).subscribe();
  }

  downloadMissionPartnerListCSV() {
    const currentDate = new Date();
    const formattedDate = this.datePipe.transform(currentDate, "yyyy-MM-dd");
    const fileName = `${formattedDate}_mission_partner_list_export.csv`;

    this.isApiLoading = true;
    this.accountService.downloadMissionPartnerListCSV(fileName).subscribe(() => (this.isApiLoading = false));
  }

  reviewMissionPartner(accountId: string) {
    this.accountService.reviewMissionPartner(accountId).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.map((item) => {
            if (item.accountId.internalId === accountId) {
              item.statuses.push(AccountStatus.REVIEWED);
            }
            return item;
          });
        }
        this.notificationService.success("", this.translateService.instant("admin.review-mission-partner.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  discardReviewMissionPartner(accountId: string) {
    this.accountService.discardReviewMissionPartner(accountId).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.map((item) => {
            if (item.accountId.internalId === accountId) {
              item.statuses = item.statuses.filter((status) => status !== AccountStatus.REVIEWED);
            }
            return item;
          });
        }
        this.notificationService.success(
          "",
          this.translateService.instant("admin.discard-review-mission-partner.success"),
        );
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  setSelfServiceStatusMissionPartner(accountId: string) {
    this.accountService.setSelfServiceStatusMissionPartner(accountId).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.map((item) => {
            if (item.accountId.internalId === accountId) {
              item.statuses.push(AccountStatus.SELF_SERVICE);
            }
            return item;
          });
        }
        this.notificationService.success(
          "",
          this.translateService.instant("admin.set-self-service-status-mission-partner.success"),
        );
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  removeSelfServiceStatusMissionPartner(accountId: string) {
    this.accountService.removeSelfServiceStatusMissionPartner(accountId).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.map((item) => {
            if (item.accountId.internalId === accountId) {
              item.statuses = item.statuses.filter((status) => status !== AccountStatus.SELF_SERVICE);
            }
            return item;
          });
        }
        this.notificationService.success(
          "",
          this.translateService.instant("admin.remove-self-service-status-mission-partner.success"),
        );
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  setPassiveStatusMissionPartner(accountId: string) {
    this.accountService.setPassiveStatusMissionPartner(accountId).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.map((item) => {
            if (item.accountId.internalId === accountId) {
              item.statuses.push(AccountStatus.PASSIVE);
            }
            return item;
          });
        }
        this.notificationService.success(
          "",
          this.translateService.instant("admin.set-passive-status-mission-partner.success"),
        );
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  removePassiveStatusMissionPartner(accountId: string) {
    this.accountService.removePassiveStatusMissionPartner(accountId).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.map((item) => {
            if (item.accountId.internalId === accountId) {
              item.statuses = item.statuses.filter((status) => status !== AccountStatus.PASSIVE);
            }
            return item;
          });
        }
        this.notificationService.success(
          "",
          this.translateService.instant("admin.remove-passive-status-mission-partner.success"),
        );
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  setPremiumStatusMissionPartner(accountId: string) {
    this.accountService.setPremiumStatusMissionPartner(accountId).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.map((item) => {
            if (item.accountId.internalId === accountId) {
              item.statuses.push(AccountStatus.PREMIUM);
            }
            return item;
          });
        }
        this.notificationService.success(
          "",
          this.translateService.instant("admin.set-premium-status-mission-partner.success"),
        );
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  removePremiumStatusMissionPartner(accountId: string) {
    this.accountService.removePremiumStatusMissionPartner(accountId).subscribe((success) => {
      if (success) {
        const item = this.tableState.data?.items?.find((item) => item.accountId.internalId === accountId);
        if (item) {
          item.statuses = item.statuses.filter((status) => status !== AccountStatus.PREMIUM);
        }
        this.notificationService.success("", this.translateService.instant("admin.remove-premium-status.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  setPublisherStatusMissionPartner(accountId: string) {
    this.accountService.setPublisherStatusMissionPartner(accountId).subscribe((success) => {
      if (success) {
        const item = this.tableState.data?.items?.find((item) => item.accountId.internalId === accountId);
        if (item) {
          item.statuses.push(AccountStatus.PUBLISHER);
        }
        this.notificationService.success("", this.translateService.instant("admin.set-publisher-status.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  removePublisherStatusMissionPartner(accountId: string) {
    this.accountService.removePublisherStatusMissionPartner(accountId).subscribe((success) => {
      if (success) {
        const item = this.tableState.data?.items?.find((item) => item.accountId.internalId === accountId);
        if (item) {
          item.statuses = item.statuses.filter((status) => status !== AccountStatus.PUBLISHER);
        }
        this.notificationService.success("", this.translateService.instant("admin.remove-publisher-status.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  setStageMissionPartner(accountId: string, stage: number) {
    this.accountService.setStageMissionPartner(accountId, stage).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.map((item) => {
            if (item.accountId.internalId === accountId) {
              item.stage = stage;
            }
            return item;
          });
        }
        this.notificationService.success("", this.translateService.instant("admin.set-stage-mission-partner.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  setConfirmedStatus(missionPartner: MissionPartnerAccount) {
    this.accountService.addAccountConfirmedStatus(missionPartner.accountId.internalId).subscribe((success) => {
      if (success) {
        if (!missionPartner.statuses.includes(AccountStatus.CONFIRMED))
          missionPartner.statuses.push(AccountStatus.CONFIRMED);
        if (!missionPartner.statuses.includes(AccountStatus.REGISTERED))
          missionPartner.statuses.push(AccountStatus.REGISTERED);
        this.notificationService.success("", this.translateService.instant("admin.add-confirmed-status.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  exportMissionPartnerToBrevo(missionPartner: MissionPartnerAccount) {
    this.accountService.exportMissionPartnerProfileToBrevo(missionPartner.accountId.internalId).subscribe((success) => {
      if (success) {
        this.notificationService.success("", this.translateService.instant("profile.exported.notification"));
      }
    });
  }

  copyId(id: string) {
    const isCopySuccessful = this.clipboard.copy(id);
    if (isCopySuccessful) {
      this.notificationService.success("", this.translateService.instant("admin.copy-id.success"));
    }
  }

  getDefaultQueryParams() {
    const from = new Date(2023, 0, 1);
    const to = new Date(new Date().getFullYear(), 11, 31);

    return {
      paging: {
        page: 1,
        itemsOnPage: 50,
      },
      searchTerm: "",
      timeRange: [from, to],
    };
  }

  protected readonly getStatusShortName = getStatusShortName;
  protected readonly AccountStatus = AccountStatus;
}
